import React, { useMemo, useState } from 'react';
import SupportContact from './SupportContact';

function getGalleryUrl(galleryId) {
  const encodedGalleryId = encodeURIComponent(String(galleryId || '').trim());
  return `${window.location.origin}/g/${encodedGalleryId}`;
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = value;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textArea);

  if (!copied) {
    throw new Error('Copy command was not available');
  }
}

export default function GuestRejoinHelp({ galleryId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState('idle');
  const galleryUrl = useMemo(() => getGalleryUrl(galleryId), [galleryId]);

  const handleCopy = async () => {
    try {
      await copyText(galleryUrl);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="absolute right-4 top-4 inline-flex min-h-11 items-center gap-2 rounded-full border border-accent-30 bg-surface px-4 py-2 text-sm font-medium text-secondary shadow-sm transition-colors hover:bg-primary-10 focus:outline-none focus:ring-2 focus:ring-primary-50"
        aria-label="Open help for saving and returning to this gallery"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9a4 4 0 117.544 2c-.8 1.4-2.772 1.6-2.772 3M12 18h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Save &amp; return
      </button>

      {isOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-secondary-50 p-0 sm:items-center sm:p-4" role="presentation">
          <section
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-surface p-6 shadow-xl sm:rounded-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="guest-rejoin-title"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="guest-rejoin-title" className="text-xl font-semibold text-secondary">Save this gallery</h2>
                <p className="mt-1 text-sm text-secondary-70">Keep the link handy so you can return to the wedding photos later.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="min-h-11 min-w-11 rounded-full text-2xl text-secondary-70 hover:bg-primary-10"
                aria-label="Close gallery help"
              >
                &times;
              </button>
            </div>

            <div className="mt-5 rounded-xl border border-accent-30 bg-neutral-30 p-4">
              <p className="break-all text-sm text-secondary-80">{galleryUrl}</p>
              <button
                type="button"
                onClick={handleCopy}
                className="mt-3 inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-primary px-4 py-2 font-medium text-surface hover:bg-primary-90 focus:outline-none focus:ring-2 focus:ring-primary-50"
              >
                Copy gallery link
              </button>
              <p className="mt-2 min-h-5 text-sm text-secondary-70" role="status" aria-live="polite">
                {copyStatus === 'copied' ? 'Gallery link copied.' : null}
                {copyStatus === 'failed' ? 'Copy failed. Press and hold the link above to copy it.' : null}
              </p>
            </div>

            <div className="mt-5 space-y-4 text-sm text-secondary-80">
              <div>
                <h3 className="font-semibold text-secondary">Add to your Home Screen</h3>
                <p className="mt-1">On iPhone or iPad, open Safari's Share menu and choose Add to Home Screen. On Android, open the browser menu and choose Install app or Add to Home screen.</p>
              </div>
              <div>
                <h3 className="font-semibold text-secondary">Returning later</h3>
                <p className="mt-1">Open the saved link and enter the wedding invite code again if your session has expired. This page never displays or copies the invite code.</p>
              </div>
              <div>
                <h3 className="font-semibold text-secondary">Offline uploads</h3>
                <p className="mt-1">Uploads queued while offline will resume after you reconnect and reopen this gallery.</p>
              </div>
              <div>
                <h3 className="font-semibold text-secondary">Wrong gallery?</h3>
                <p className="mt-1">Use the link from the couple or venue for the wedding you want to join.</p>
              </div>
            </div>
            <SupportContact />
          </section>
        </div>
      ) : null}
    </>
  );
}
